import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Search, 
  Filter, 
  Brain, 
  TrendingUp, 
  Target,
  Clock,
  ArrowRight,
  BookOpen
} from 'lucide-react';
import SkillCard from '../../components/Cards/SkillCard';
import LoadingSpinner from '../../components/UI/LoadingSpinner';

const Skills = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedLevel, setSelectedLevel] = useState('all');
  const [selectedPriority, setSelectedPriority] = useState('all');
  const [isLoading, setIsLoading] = useState(false);

  // Mock skills data
  const skills = [
    {
      id: 1,
      skill_name: 'Python Programming',
      category: 'Programming',
      difficulty_level: 'intermediate',
      estimated_learning_hours: 120,
      priority_score: 0.85,
      demand_score: 0.90,
      interest_score: 0.75,
      salary_impact: 0.80,
      growth_trend: 'increasing',
      learning_resources: [
        'Python.org Official Tutorial',
        'Real Python Course',
        'Python for Data Science'
      ]
    },
    {
      id: 2,
      skill_name: 'React Development',
      category: 'Web Development',
      difficulty_level: 'intermediate',
      estimated_learning_hours: 80,
      priority_score: 0.78,
      demand_score: 0.85,
      interest_score: 0.80,
      salary_impact: 0.75,
      growth_trend: 'increasing',
      learning_resources: [
        'React Official Documentation',
        'React Bootcamp Course',
        'Advanced React Patterns'
      ]
    },
    {
      id: 3,
      skill_name: 'Machine Learning',
      category: 'Data Science',
      difficulty_level: 'advanced',
      estimated_learning_hours: 200,
      priority_score: 0.92,
      demand_score: 0.95,
      interest_score: 0.70,
      salary_impact: 0.90,
      growth_trend: 'increasing',
      learning_resources: [
        'Machine Learning Course by Andrew Ng',
        'Hands-On Machine Learning',
        'Deep Learning Specialization'
      ]
    },
    {
      id: 4,
      skill_name: 'Cloud Computing (AWS)',
      category: 'Cloud Computing',
      difficulty_level: 'intermediate',
      estimated_learning_hours: 100,
      priority_score: 0.70,
      demand_score: 0.80,
      interest_score: 0.65,
      salary_impact: 0.85,
      growth_trend: 'stable',
      learning_resources: [
        'AWS Official Training',
        'AWS Solutions Architect Course',
        'Cloud Practitioner Certification'
      ]
    },
    {
      id: 5,
      skill_name: 'Data Analysis',
      category: 'Data Science',
      difficulty_level: 'beginner',
      estimated_learning_hours: 60,
      priority_score: 0.60,
      demand_score: 0.75,
      interest_score: 0.85,
      salary_impact: 0.70,
      growth_trend: 'stable',
      learning_resources: [
        'Data Analysis with Python',
        'Excel for Data Analysis',
        'SQL for Data Analysis'
      ]
    },
    {
      id: 6,
      skill_name: 'Cybersecurity',
      category: 'Security',
      difficulty_level: 'advanced',
      estimated_learning_hours: 150,
      priority_score: 0.88,
      demand_score: 0.90,
      interest_score: 0.60,
      salary_impact: 0.95,
      growth_trend: 'increasing',
      learning_resources: [
        'Cybersecurity Fundamentals',
        'Ethical Hacking Course',
        'Security+ Certification'
      ]
    }
  ];

  const categories = ['all', 'Programming', 'Web Development', 'Data Science', 'Cloud Computing', 'Security', 'Mobile Development'];
  const levels = ['all', 'beginner', 'intermediate', 'advanced'];
  const priorities = [
    { value: 'all', label: 'All Priorities' },
    { value: 'high', label: 'High Priority (80%+)' },
    { value: 'medium', label: 'Medium Priority (60-80%)' },
    { value: 'low', label: 'Low Priority (<60%)' }
  ];

  const filteredSkills = skills.filter(skill => {
    const matchesSearch = skill.skill_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         skill.category.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || skill.category === selectedCategory;
    const matchesLevel = selectedLevel === 'all' || skill.difficulty_level === selectedLevel;
    
    let matchesPriority = true;
    if (selectedPriority !== 'all') {
      const threshold = selectedPriority === 'high' ? 0.8 : 
                       selectedPriority === 'medium' ? 0.6 : 0;
      if (selectedPriority === 'high') {
        matchesPriority = skill.priority_score >= threshold;
      } else if (selectedPriority === 'medium') {
        matchesPriority = skill.priority_score >= threshold && skill.priority_score < 0.8;
      } else {
        matchesPriority = skill.priority_score < 0.6;
      }
    }
    
    return matchesSearch && matchesCategory && matchesLevel && matchesPriority;
  });

  const handleLearn = (skill) => {
    console.log('Learning skill:', skill.skill_name);
    // Handle skill learning logic
  };

  const handleViewDetails = (skill) => {
    console.log('Viewing skill details:', skill.skill_name);
    // Handle view details logic
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Skills to Learn</h1>
          <p className="text-gray-600 mt-2">Discover skills that will boost your career with AI-powered recommendations</p>
        </div>
        <div className="flex items-center space-x-2">
          <button className="btn btn-outline btn-sm">
            <BookOpen className="h-4 w-4 mr-2" />
            Learning Path
          </button>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="card">
        <div className="card-content">
          <div className="flex flex-col lg:flex-row gap-4">
            {/* Search */}
            <div className="flex-1">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Search className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  placeholder="Search skills..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="input pl-10 w-full"
                />
              </div>
            </div>

            {/* Category Filter */}
            <div className="lg:w-48">
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="input w-full"
              >
                {categories.map(category => (
                  <option key={category} value={category}>
                    {category === 'all' ? 'All Categories' : category}
                  </option>
                ))}
              </select>
            </div>

            {/* Level Filter */}
            <div className="lg:w-48">
              <select
                value={selectedLevel}
                onChange={(e) => setSelectedLevel(e.target.value)}
                className="input w-full"
              >
                {levels.map(level => (
                  <option key={level} value={level}>
                    {level === 'all' ? 'All Levels' : level.charAt(0).toUpperCase() + level.slice(1)}
                  </option>
                ))}
              </select>
            </div>

            {/* Priority Filter */}
            <div className="lg:w-48">
              <select
                value={selectedPriority}
                onChange={(e) => setSelectedPriority(e.target.value)}
                className="input w-full"
              >
                {priorities.map(priority => (
                  <option key={priority.value} value={priority.value}>
                    {priority.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Filter Button */}
            <button className="btn btn-outline">
              <Filter className="h-4 w-4 mr-2" />
              More Filters
            </button>
          </div>
        </div>
      </div>

      {/* Results */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-600">
          Showing {filteredSkills.length} of {skills.length} skills
        </p>
        <div className="flex items-center space-x-2">
          <span className="text-sm text-gray-600">Sort by:</span>
          <select className="input text-sm">
            <option value="priority">Priority</option>
            <option value="demand">Demand</option>
            <option value="difficulty">Difficulty</option>
            <option value="hours">Learning Hours</option>
          </select>
        </div>
      </div>

      {/* Skills Grid */}
      {isLoading ? (
        <div className="flex items-center justify-center h-64">
          <LoadingSpinner size="lg" />
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredSkills.map((skill, index) => (
            <motion.div
              key={skill.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
            >
              <SkillCard
                skill={skill}
                onLearn={handleLearn}
                onViewDetails={handleViewDetails}
              />
            </motion.div>
          ))}
        </div>
      )}

      {/* Load More */}
      {filteredSkills.length > 0 && (
        <div className="text-center">
          <button className="btn btn-outline">
            Load More Skills
            <ArrowRight className="h-4 w-4 ml-2" />
          </button>
        </div>
      )}

      {/* Empty State */}
      {filteredSkills.length === 0 && !isLoading && (
        <div className="text-center py-12">
          <Brain className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No skills found</h3>
          <p className="text-gray-600 mb-4">Try adjusting your search or filter criteria</p>
          <button
            onClick={() => {
              setSearchTerm('');
              setSelectedCategory('all');
              setSelectedLevel('all');
              setSelectedPriority('all');
            }}
            className="btn btn-primary"
          >
            Clear Filters
          </button>
        </div>
      )}

      {/* Learning Path Suggestion */}
      <div className="card bg-gradient-to-r from-primary-50 to-secondary-50 border-primary-200">
        <div className="card-content">
          <div className="flex items-center space-x-3 mb-4">
            <Target className="h-6 w-6 text-primary-600" />
            <h3 className="text-lg font-semibold text-gray-900">AI Learning Path</h3>
          </div>
          <p className="text-gray-600 mb-4">
            Based on your profile and career goals, we recommend focusing on these skills in order:
          </p>
          <div className="flex flex-wrap gap-2">
            {['Python Programming', 'Data Analysis', 'Machine Learning', 'Cloud Computing'].map((skill, index) => (
              <div key={skill} className="flex items-center space-x-2 bg-white px-3 py-2 rounded-lg border">
                <span className="text-sm font-medium text-primary-600">#{index + 1}</span>
                <span className="text-sm text-gray-700">{skill}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Skills;
